import { motion } from 'framer-motion'
import { ArrowLeft, Shield, FileText, Database, BarChart3, Lock, Heart, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useEffect } from 'react'

export default function PrivacyPolicy() {
  const { t } = useTranslation()

  // Sayfa yüklendiğinde en üste scroll
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="text-white">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center p-4 sm:p-6"
      >
        <div className="flex items-center">
          <div className="relative">
            <img src="/assets/logo.png" alt="Scraible Logo" className="w-12 h-12" />
          </div>
          <h1 className="text-xl font-bold text-gradient">Scraible</h1>
        </div>
        <Link
          to="/"
          className="btn-primary flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg hover:from-violet-600 hover:to-purple-600 transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Home</span>
        </Link>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="relative mb-6">
            <Shield className="w-16 h-16 text-violet-400 mx-auto" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gradient mb-4">Privacy Policy</h1>
          <p className="text-white/70 text-lg">Your privacy is important to us</p>
          <p className="text-white/50 text-sm mt-2">Last updated: September 25, 2025</p>
        </motion.div>

        {/* Content Cards */}
        <div className="space-y-8">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="card-gradient p-6 floating-card neon-glow"
          >
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
              <FileText className="w-6 h-6 text-violet-400" />
              <span>Introduction</span>
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.
            </p>
            <p className="text-white/80 leading-relaxed">
              We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.
            </p>
          </motion.div>

          {/* Definitions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="card-gradient p-6 floating-card neon-glow"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <FileText className="w-6 h-6 text-violet-400" />
              <span>Interpretation and Definitions</span>
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-violet-300 mb-3">Interpretation</h3>
                <p className="text-white/80 leading-relaxed">
                  The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-violet-300 mb-3">Definitions</h3>
                <p className="text-white/80 leading-relaxed mb-4">For the purposes of this Privacy Policy:</p>
                
                <div className="grid gap-4">
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Account</h4>
                    <p className="text-white/70 text-sm">means a unique account created for You to access our Service or parts of our Service.</p>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Company</h4>
                    <p className="text-white/70 text-sm">refers to Collabry Software and IT Inc., Barbaros, Şebboy Sk. No:4/1 İç Kapı No:1, 34758 Ataşehir/İstanbul.</p>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Cookies</h4>
                    <p className="text-white/70 text-sm">are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</p>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Personal Data</h4>
                    <p className="text-white/70 text-sm">is any information that relates to an identified or identifiable individual.</p>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Service</h4>
                    <p className="text-white/70 text-sm">refers to the Website.</p>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Website</h4>
                    <p className="text-white/70 text-sm">
                      refers to Scraible, accessible from{' '}
                      <a 
                        href="https://app.scraible.com/" 
                        className="text-violet-400 hover:text-violet-300 transition-colors" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        https://app.scraible.com/
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Data Collection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="card-gradient p-6 floating-card neon-glow"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <Database className="w-6 h-6 text-violet-400" />
              <span>Collecting and Using Your Personal Data</span>
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-violet-300 mb-3">Types of Data Collected</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 p-4 rounded-lg border border-violet-400/30">
                    <h4 className="font-semibold text-violet-300 mb-2">Personal Data</h4>
                    <p className="text-white/80 text-sm mb-3">While using Our Service, We may ask You to provide Us with certain personally identifiable information:</p>
                    <ul className="text-white/70 text-sm space-y-1">
                      <li>• Email address</li>
                      <li>• Usage Data</li>
                    </ul>
                  </div>
                  
                  <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 p-4 rounded-lg border border-emerald-400/30">
                    <h4 className="font-semibold text-emerald-300 mb-2">Usage Data</h4>
                    <p className="text-white/80 text-sm">Usage Data is collected automatically when using the Service, including IP address, browser type, pages visited, and device information.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Data Usage */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="card-gradient p-6 floating-card neon-glow"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <BarChart3 className="w-6 h-6 text-violet-400" />
              <span>Use of Your Personal Data</span>
            </h2>
            
            <div className="grid gap-4">
              <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">To provide and maintain our Service</h4>
                <p className="text-white/70 text-sm">including to monitor the usage of our Service.</p>
              </div>
              
              <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">To manage Your Account</h4>
                <p className="text-white/70 text-sm">to manage Your registration as a user of the Service.</p>
              </div>
              
              <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">To contact You</h4>
                <p className="text-white/70 text-sm">by email, telephone calls, SMS, or other equivalent forms of electronic communication.</p>
              </div>
              
              <div className="bg-white/5 p-4 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">To provide You with news and special offers</h4>
                <p className="text-white/70 text-sm">about other goods, services and events which we offer.</p>
              </div>
            </div>
          </motion.div>

          {/* Security */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="card-gradient p-6 floating-card neon-glow"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <Lock className="w-6 h-6 text-violet-400" />
              <span>Security of Your Personal Data</span>
            </h2>
            <p className="text-white/80 leading-relaxed">
              The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.
            </p>
          </motion.div>

          {/* Children's Privacy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="card-gradient p-6 floating-card neon-glow"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <Heart className="w-6 h-6 text-violet-400" />
              <span>Children's Privacy</span>
            </h2>
            <p className="text-white/80 leading-relaxed">
              Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us.
            </p>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="card-gradient p-6 floating-card neon-glow"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
              <Mail className="w-6 h-6 text-violet-400" />
              <span>Contact Us</span>
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">If you have any questions about this Privacy Policy, You can contact us:</p>
            <div className="space-y-2">
              <p className="text-white/70">
                By email: <a href="mailto:app@collabry.io" className="text-violet-400 hover:text-violet-300 transition-colors">app@collabry.io</a>
              </p>
              <p className="text-white/70">
                By visiting: <a href="https://collabry.io" className="text-violet-400 hover:text-violet-300 transition-colors" target="_blank" rel="noopener noreferrer">https://collabry.io</a>
              </p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.6 }}
        className="mt-16 pt-8 border-t border-white/10"
      >
        <div className="text-center px-4">
          <div className="text-xs text-white/40">
            © 2024 Scraible. All rights reserved.
          </div>
        </div>
      </motion.footer>
    </div>
  )
}
